import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";

export function PeopleListPage({ endpoint, title, subtitle, kindLabel }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", notes: "" });

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/${endpoint}`); setItems(data); }
    catch (e) {
      // eslint-disable-next-line no-console
      console.warn(`[${endpoint}] load failed:`, e?.message || e);
      toast.error("Could not load");
    }
  }, [endpoint]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ name: "", phone: "", email: "", notes: "" }); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ name: p.name || "", phone: p.phone || "", email: p.email || "", notes: p.notes || "" }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/${endpoint}/${editing.id}`, form);
      else await api.post(`/${endpoint}`, form);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove?")) return;
    try { await api.delete(`/${endpoint}/${id}`); load(); } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-[#8A847A]">Team</div>
          <h1 className="heading text-3xl sm:text-4xl font-bold text-[#292723] mt-2">{title}</h1>
          <p className="text-sm text-[#5C5850] mt-1.5">{subtitle}</p>
        </div>
        <Button onClick={openNew} className="bg-[#4A6B5D] hover:bg-[#3E594D]" data-testid={`add-${kindLabel}-btn`}>
          <Plus className="h-4 w-4 mr-2"/> Add {kindLabel}
        </Button>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.length === 0 && (
          <div className="col-span-full bg-white border border-[#E5E0D8] rounded-md p-10 text-center text-[#8A847A] text-sm">
            No {kindLabel}s yet.
          </div>
        )}
        {items.map((p) => (
          <div key={p.id} className="bg-white border border-[#E5E0D8] rounded-md p-4 flex items-start justify-between gap-3" data-testid={`${kindLabel}-${p.id}`}>
            <div className="min-w-0">
              <div className="heading font-semibold text-[#292723]">{p.name}</div>
              <div className="text-sm text-[#5C5850] mt-0.5">{p.email || "—"}</div>
              <div className="text-sm text-[#5C5850]">{p.phone || "—"}</div>
              {typeof p.facility_count === "number" && (
                <div className="text-xs text-[#8A847A] mt-2">{p.facility_count} facilities</div>
              )}
              {p.notes && <div className="text-xs text-[#5C5850] mt-2 whitespace-pre-wrap">{p.notes}</div>}
            </div>
            <div className="flex gap-1 shrink-0">
              <Button variant="ghost" size="sm" onClick={() => openEdit(p)}><Pencil className="h-4 w-4"/></Button>
              <Button variant="ghost" size="sm" onClick={() => remove(p.id)} className="text-[#C17D6E]"><Trash2 className="h-4 w-4"/></Button>
            </div>
          </div>
        ))}
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md bg-white overflow-y-auto">
          <SheetHeader><SheetTitle className="heading text-2xl">{editing ? "Edit" : "Add"} {kindLabel}</SheetTitle></SheetHeader>
          <form onSubmit={submit} className="mt-6 space-y-4">
            {[["name","Name",true],["phone","Phone"],["email","Email"]].map(([k, l, req]) => (
              <div key={k}>
                <Label className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">{l}</Label>
                <Input required={req} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1.5 h-10 bg-white border-[#E5E0D8]" />
              </div>
            ))}
            <div>
              <Label className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="mt-1.5 bg-white border-[#E5E0D8]" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#4A6B5D] hover:bg-[#3E594D]">Save</Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
}

export default PeopleListPage;
